/**
 * The activity ribbon: who spoke when, and the seek bar, as one object.
 *
 * It is deliberately not "a progress bar, and separately a chart". The shape of
 * the conversation IS the timeline — a lane per speaker, a bar wherever that
 * person was speaking — so dragging through it is dragging through the
 * conversation, and the playhead is always crossing something meaningful.
 *
 * Drawn with positioned elements rather than SVG. Percentages make it
 * responsive for free with no viewBox and no `preserveAspectRatio="none"`,
 * which would stretch every rounded corner into an ellipse.
 *
 * The data is the `activity` artifact: a few hundred bytes of VAD segments that
 * arrive with the transcript. Nothing here decodes a single audio sample — a
 * real waveform would mean downloading and decoding the whole recording to draw
 * something that says less.
 */

import * as React from 'react'
import { formatDuration } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { Speaker } from '../speakers'
import { useClockEffect, type Player } from '../use-player'

export interface TimelineLane {
  speaker: Speaker
  segments: { start_ms: number; end_ms: number }[]
}

export function Timeline({
  player,
  lanes,
  durationMs,
  laneHeight = 12,
  showAxis = true,
  className,
}: {
  player: Player
  lanes: TimelineLane[]
  durationMs: number
  /**
   * The fallback lane height, in px.
   *
   * A caller that needs the height to CHANGE with the viewport sets
   * `--era-lane` in its class list instead (`[--era-lane:8px] sm:[--era-lane:12px]`)
   * and leaves this alone. A number cannot be responsive without a JavaScript
   * breakpoint, and a JavaScript breakpoint next to a Tailwind one is two
   * sources of truth for the same decision — they drift, and the drift shows up
   * as a ribbon that is the wrong height for one frame after every rotation.
   */
  laneHeight?: number
  showAxis?: boolean
  className?: string
}) {
  const trackRef = React.useRef<HTMLDivElement | null>(null)
  const playheadRef = React.useRef<HTMLDivElement | null>(null)
  const [hoverMs, setHoverMs] = React.useState<number | null>(null)
  const scrubbing = React.useRef(false)

  /* -- the playhead moves without a React render ---------------------------- */
  useClockEffect(
    player,
    (timeMs) => {
      const pct = durationMs > 0 ? Math.min(100, (timeMs / durationMs) * 100) : 0
      if (playheadRef.current) playheadRef.current.style.transform = `translateX(${pct}%)`
      const track = trackRef.current
      if (track) {
        // Written straight to the attribute for the same reason: a screen
        // reader needs the value, but nothing on screen has to re-render for it.
        track.setAttribute('aria-valuenow', String(Math.round(timeMs)))
        track.setAttribute('aria-valuetext', formatDuration(timeMs))
      }
    },
    [durationMs],
  )

  /* -- scrubbing ------------------------------------------------------------ */

  const msAtClientX = React.useCallback(
    (clientX: number): number => {
      const rect = trackRef.current?.getBoundingClientRect()
      if (!rect || rect.width === 0) return 0
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
      return ratio * durationMs
    },
    [durationMs],
  )

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (durationMs <= 0) return
    scrubbing.current = true
    // Capture on the track, so a drag that leaves the element vertically — which
    // is most drags, because the ribbon is 40px tall — keeps scrubbing.
    event.currentTarget.setPointerCapture(event.pointerId)
    player.seekMs(msAtClientX(event.clientX))
  }

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const ms = msAtClientX(event.clientX)
    setHoverMs(ms)
    if (scrubbing.current) player.seekMs(ms)
  }

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!scrubbing.current) return
    scrubbing.current = false
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 30_000 : 5_000
    switch (event.key) {
      case 'ArrowRight':
        player.nudge(step)
        break
      case 'ArrowLeft':
        player.nudge(-step)
        break
      case 'Home':
        player.seekMs(0)
        break
      case 'End':
        player.seekMs(durationMs)
        break
      case ' ':
      case 'Enter':
        player.toggle()
        break
      default:
        return
    }
    event.preventDefault()
  }

  const ticks = React.useMemo(() => buildTicks(durationMs), [durationMs])
  const hoverPct = hoverMs !== null && durationMs > 0 ? (hoverMs / durationMs) * 100 : null

  return (
    <div className={cn('select-none', className)}>
      <div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        aria-label="Playback position"
        aria-valuemin={0}
        aria-valuemax={Math.round(durationMs)}
        aria-valuenow={0}
        aria-orientation="horizontal"
        className={cn(
          'relative cursor-pointer touch-none rounded-lg',
          // A two-speaker ribbon is 28px tall, which is a miss target for a
          // thumb and — worse — a target you have to hit while dragging. The
          // padding is inside the element that `getBoundingClientRect()` is
          // measured from, but only its WIDTH is used to convert a pointer x
          // into a time, so a taller box costs nothing in accuracy and buys
          // the whole 44px in the axis a drag actually strays in.
          'max-sm:py-2',
          durationMs <= 0 && 'pointer-events-none opacity-60',
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => setHoverMs(null)}
        onKeyDown={onKeyDown}
      >
        <div className="flex flex-col gap-1">
          {(lanes.length > 0 ? lanes : [null]).map((lane, index) => (
            <div
              key={lane ? lane.speaker.identity : `empty-${index}`}
              className="relative w-full overflow-hidden rounded-full bg-surface-2"
              style={{ height: `var(--era-lane, ${laneHeight}px)` }}
            >
              {lane?.segments.map((segment, segmentIndex) => {
                const left = durationMs > 0 ? (segment.start_ms / durationMs) * 100 : 0
                const width =
                  durationMs > 0 ? ((segment.end_ms - segment.start_ms) / durationMs) * 100 : 0
                return (
                  <span
                    key={`${segment.start_ms}-${segmentIndex}`}
                    className="absolute inset-y-0 rounded-full"
                    style={{
                      left: `${left}%`,
                      // A segment shorter than a pixel still has to be visible:
                      // a 300 ms "mm-hm" in a 90-minute call is 0.005% wide.
                      width: `max(2px, ${width}%)`,
                      backgroundColor: lane.speaker.color,
                    }}
                    title={`${lane.speaker.displayName} · ${formatDuration(segment.start_ms)}–${formatDuration(segment.end_ms)}`}
                  />
                )
              })}
            </div>
          ))}
        </div>

        {/* Hover readout. Rendered above the playhead so a slow scrub still
            shows the time it would land on. */}
        {hoverPct !== null ? (
          <div
            className="pointer-events-none absolute -top-6 z-10 -translate-x-1/2 rounded bg-ink px-1.5 py-0.5 text-[0.6875rem] font-medium text-paper tabular"
            style={{ left: `${hoverPct}%` }}
          >
            {formatDuration(hoverMs)}
          </div>
        ) : null}

        {/* The playhead. One absolutely positioned full-width layer, moved with
            a percentage transform — no layout, no render. */}
        <div ref={playheadRef} className="pointer-events-none absolute inset-y-0 left-0 w-full">
          <span className="absolute -top-1 -bottom-1 left-0 w-[2px] rounded-full bg-ink" />
          <span className="absolute -top-2 left-0 size-2 -translate-x-[3px] rounded-full bg-ink" />
        </div>
      </div>

      {showAxis ? (
        <div className="relative mt-1 h-3.5 sm:mt-2 sm:h-4">
          {ticks.map((tick) => (
            <span
              key={tick}
              className="absolute top-0 -translate-x-1/2 text-[0.6875rem] tabular text-ink-dim"
              style={{ left: `${(tick / durationMs) * 100}%` }}
            >
              {formatDuration(tick)}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Round tick positions, roughly six of them.
 *
 * Ticks land on 5 s / 15 s / 30 s / 1 min / 5 min boundaries rather than on
 * `duration / 6`, because `2:41` and `5:22` as axis labels make a reader do
 * arithmetic to place anything.
 */
function buildTicks(durationMs: number): number[] {
  if (!Number.isFinite(durationMs) || durationMs <= 0) return []
  const steps = [5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600]
  const target = durationMs / 1000 / 6
  const step = (steps.find((candidate) => candidate >= target) ?? 3600) * 1000

  const ticks: number[] = []
  // The last tick is dropped when it would sit within 6% of the right edge:
  // its label would be half outside the container.
  for (let at = 0; at <= durationMs - durationMs * 0.06; at += step) ticks.push(at)
  return ticks
}
